/**
 * PropuestasSection.tsx — Sección «Propuestas» de la ficha (P2RED-S1-3a).
 *
 * Vista POR ELEMENTO de las propuestas que le afectan, en dos formas:
 *  - Propuestas de ámbito elemento (utf8/chars/format/coords/ia): diff
 *    original→propuesto (texto) o par X/Y con Δ en metros (coordenada),
 *    con acciones aceptar/rechazar o chip «auto» reversible (§5.4, §6.2).
 *  - Tarjetas de REGLA β (abreviatura/diccionario, §11.5 CRITERIOS):
 *    cabecera «Regla del documento» + chip de alcance «N elementos ·
 *    M excepciones» + resultado efectivo en ESTE elemento + enlace
 *    «Hacer excepción en este elemento…» que despliega ✓/✎/✗
 *    (crítica 1 del AUDIT, caso «Cañada Sur»).
 *
 * El componente es presentacional: el estado (status y overrides) vive en
 * el contenedor (Step2Page) y llega ya resuelto en cada `Propuesta`.
 * `origen` NO se muestra en la UI principal (§6.3) — solo el motivo bajo
 * «Ver motivo» cuando existe.
 *
 * Cierra `OBS-P2-EXCEPCION-POR-ELEMENTO-REGLAS` (sev B).
 */

import { useState, type CSSProperties, type FC } from 'react';
import { colors, typography, spacing } from '@/design-system/v2';
import {
  deltaMetros,
  esRegla,
  propuestasDeElemento,
  valorEfectivo,
  type CoordPair,
  type ExcepcionElemento,
  type Propuesta,
  type PropuestaStatus,
} from './propuestas';

export interface PropuestasSectionProps {
  /** Lista completa del documento; la sección filtra por `infraId`. */
  propuestas: readonly Propuesta[];
  /** Elemento seleccionado en la ficha. */
  infraId: string;
  /** Cambio de estado de una propuesta de ámbito elemento (aceptar/rechazar/deshacer). */
  onEstado?: (propuestaId: string, status: PropuestaStatus) => void;
  /** Alta/edición de excepción por elemento sobre una regla; `null` = quitar la excepción. */
  onExcepcion?: (propuestaId: string, infraId: string, excepcion: ExcepcionElemento | null) => void;
}

// ============================================================================
// STYLES (DS v2 · sin glow)
// ============================================================================

const listaStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: spacing[2],
};

const tarjetaStyle: CSSProperties = {
  border: `1px solid ${colors.border.DEFAULT}`,
  borderRadius: '6px',
  padding: spacing[2],
  fontSize: typography.fontSize.xs,
};

const cabTarjetaStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: spacing[2],
  flexWrap: 'wrap',
  marginBottom: spacing[1],
};

const chipBase: CSSProperties = {
  display: 'inline-block',
  padding: `0 ${spacing[1]}`,
  borderRadius: '4px',
  fontSize: typography.fontSize.xs,
  fontWeight: 600,
  lineHeight: 1.6,
};

const CHIP_ESTADO: Record<PropuestaStatus, { texto: string; css: CSSProperties }> = {
  auto: { texto: 'Aplicada automáticamente', css: { ...chipBase, color: colors.info, background: colors.infoMuted } },
  pending: { texto: 'Pendiente', css: { ...chipBase, color: colors.warning, background: colors.warningMuted } },
  approved: { texto: 'Aceptada', css: { ...chipBase, color: colors.success, background: colors.successMuted } },
  rejected: { texto: 'Rechazada', css: { ...chipBase, color: colors.error, background: colors.errorMuted } },
};

const originalStyle: CSSProperties = {
  textDecoration: 'line-through',
  color: colors.fg.muted,
  wordBreak: 'break-word',
};

const propuestoStyle: CSSProperties = {
  color: colors.success,
  fontWeight: 600,
  wordBreak: 'break-word',
};

const monoStyle: CSSProperties = {
  fontFamily: typography.fontFamily.mono,
};

const botonStyle: CSSProperties = {
  padding: `2px ${spacing[2]}`,
  border: `1px solid ${colors.border.DEFAULT}`,
  borderRadius: '4px',
  background: 'transparent',
  color: colors.fg.primary,
  fontFamily: typography.fontFamily.sans,
  fontSize: typography.fontSize.xs,
  cursor: 'pointer',
};

const enlaceStyle: CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  color: colors.primary,
  fontFamily: typography.fontFamily.sans,
  fontSize: typography.fontSize.xs,
  cursor: 'pointer',
  textDecoration: 'underline',
};

const motivoStyle: CSSProperties = {
  marginTop: spacing[1],
  color: colors.fg.muted,
};

const vacioStyle: CSSProperties = {
  margin: 0,
  color: colors.fg.muted,
  fontSize: typography.fontSize.xs,
};

const inputStyle: CSSProperties = {
  flex: 1,
  minWidth: '120px',
  padding: `2px ${spacing[1]}`,
  border: `1px solid ${colors.border.DEFAULT}`,
  borderRadius: '4px',
  background: 'transparent',
  color: colors.fg.primary,
  fontFamily: typography.fontFamily.sans,
  fontSize: typography.fontSize.xs,
};

// ============================================================================
// HELPERS DE RENDER
// ============================================================================

const formatearPar = (c: CoordPair): string => `${c.x.toFixed(2)}, ${c.y.toFixed(2)}`;

const valorATexto = (v: string | CoordPair): string =>
  typeof v === 'string' ? v : formatearPar(v);

/** Diff original→propuesto; en coordenadas añade Δ en metros (§5.4). */
const DiffValor: FC<{ p: Propuesta }> = ({ p }) => {
  if (p.valueKind === 'coordenada') {
    const o = p.original as CoordPair;
    const n = p.propuesto as CoordPair;
    return (
      <div style={monoStyle}>
        <span style={originalStyle}>{formatearPar(o)}</span>{' → '}
        <span style={propuestoStyle}>{formatearPar(n)}</span>{' '}
        <span style={{ color: colors.fg.muted }}>Δ {deltaMetros(o, n).toFixed(1)} m</span>
      </div>
    );
  }
  return (
    <div>
      <span style={originalStyle}>{valorATexto(p.original)}</span>{' → '}
      <span style={propuestoStyle}>{valorATexto(p.propuesto)}</span>
    </div>
  );
};

/** Motivo del detector, plegado (visible al consultar — §3 `reason`). */
const Motivo: FC<{ reason?: string }> = ({ reason }) =>
  reason == null ? null : (
    <details style={motivoStyle}>
      <summary style={{ cursor: 'pointer' }}>Ver motivo</summary>
      <span>{reason}</span>
    </details>
  );

// ============================================================================
// TARJETA · PROPUESTA DE ÁMBITO ELEMENTO
// ============================================================================

const TarjetaElemento: FC<{
  p: Propuesta;
  onEstado?: PropuestasSectionProps['onEstado'];
}> = ({ p, onEstado }) => {
  const chip = CHIP_ESTADO[p.status];
  return (
    <div style={tarjetaStyle} data-propuesta-id={p.id}>
      <div style={cabTarjetaStyle}>
        <span style={{ fontWeight: 600, color: colors.fg.primary }}>{p.field}</span>
        <span style={chip.css}>{chip.texto}</span>
        <span style={{ color: colors.fg.muted }}>confianza {p.confidence}</span>
      </div>
      <DiffValor p={p} />
      <Motivo reason={p.reason} />
      {onEstado != null && (
        <div style={{ display: 'flex', gap: spacing[1], marginTop: spacing[1] }}>
          {p.status === 'pending' && (
            <>
              <button type="button" style={botonStyle} onClick={() => onEstado(p.id, 'approved')}>
                Aceptar
              </button>
              <button type="button" style={botonStyle} onClick={() => onEstado(p.id, 'rejected')}>
                Rechazar
              </button>
            </>
          )}
          {p.status !== 'pending' && (
            <button type="button" style={botonStyle} onClick={() => onEstado(p.id, 'pending')}>
              Deshacer
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// TARJETA · REGLA β (revelado progresivo, §11.5)
// ============================================================================

const TarjetaRegla: FC<{
  p: Propuesta;
  infraId: string;
  onExcepcion?: PropuestasSectionProps['onExcepcion'];
}> = ({ p, infraId, onExcepcion }) => {
  const [desplegada, setDesplegada] = useState(false);
  const [editando, setEditando] = useState(false);
  const [texto, setTexto] = useState('');
  const nExcepciones = Object.keys(p.overrides ?? {}).length;
  const excepcion = p.overrides?.[infraId];
  const efectivo = valorEfectivo(p, infraId);
  const sinCambio = efectivo === p.original;

  const fijar = (e: ExcepcionElemento | null) => {
    onExcepcion?.(p.id, infraId, e);
    setDesplegada(false);
    setEditando(false);
  };

  return (
    <div style={tarjetaStyle} data-propuesta-id={p.id}>
      <div style={cabTarjetaStyle}>
        <span style={{ fontWeight: 600, color: colors.fg.primary }}>Regla del documento</span>
        <span style={{ ...chipBase, color: colors.fg.muted, border: `1px solid ${colors.border.DEFAULT}` }}>
          {p.infraIds?.length ?? 0} elementos · {nExcepciones} excepciones
        </span>
      </div>
      <div>
        <span style={originalStyle}>{valorATexto(p.original)}</span>{' → '}
        <span style={sinCambio ? { color: colors.fg.muted } : propuestoStyle}>
          {sinCambio ? 'sin cambio en este elemento' : valorATexto(efectivo)}
        </span>
      </div>
      <Motivo reason={p.reason} />
      {excepcion != null && (
        <div style={{ display: 'flex', gap: spacing[1], alignItems: 'center', marginTop: spacing[1] }}>
          <span style={{ ...chipBase, color: colors.warning, background: colors.warningMuted }}>
            Excepción: {excepcion.status === 'edited' ? `editada («${excepcion.valor}»)` : excepcion.status === 'approved' ? 'aceptada' : 'rechazada'}
          </span>
          {onExcepcion != null && (
            <button type="button" style={enlaceStyle} onClick={() => fijar(null)}>
              Quitar excepción
            </button>
          )}
        </div>
      )}
      {onExcepcion != null && excepcion == null && !desplegada && (
        <button type="button" style={{ ...enlaceStyle, marginTop: spacing[1] }} onClick={() => setDesplegada(true)}>
          Hacer excepción en este elemento…
        </button>
      )}
      {onExcepcion != null && desplegada && (
        <div style={{ display: 'flex', gap: spacing[1], marginTop: spacing[1], flexWrap: 'wrap', alignItems: 'center' }}>
          {!editando ? (
            <>
              <button type="button" style={botonStyle} onClick={() => fijar({ status: 'approved' })}>
                ✓ Aceptar
              </button>
              <button
                type="button"
                style={botonStyle}
                onClick={() => { setTexto(valorATexto(p.propuesto)); setEditando(true); }}
              >
                ✎ Editar
              </button>
              <button type="button" style={botonStyle} onClick={() => fijar({ status: 'rejected' })}>
                ✗ Rechazar
              </button>
            </>
          ) : (
            <>
              <input
                type="text"
                style={inputStyle}
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
                aria-label="Valor de la excepción"
              />
              <button
                type="button"
                style={botonStyle}
                onClick={() => fijar({ status: 'edited', valor: texto })}
              >
                Guardar
              </button>
              <button type="button" style={botonStyle} onClick={() => setEditando(false)}>
                Cancelar
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// SECCIÓN
// ============================================================================

export const PropuestasSection: FC<PropuestasSectionProps> = ({
  propuestas,
  infraId,
  onEstado,
  onExcepcion,
}) => {
  const delElemento = propuestasDeElemento(propuestas, infraId);
  if (delElemento.length === 0) {
    return <p style={vacioStyle}>Sin propuestas para este elemento.</p>;
  }
  return (
    <div style={listaStyle}>
      {delElemento.map((p) =>
        esRegla(p) ? (
          <TarjetaRegla key={p.id} p={p} infraId={infraId} onExcepcion={onExcepcion} />
        ) : (
          <TarjetaElemento key={p.id} p={p} onEstado={onEstado} />
        ),
      )}
    </div>
  );
};

export default PropuestasSection;
